// src/SocketContext.js - FINALISÉ avec deleteNotification et CORRECTION DU CLEANUP

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { getAuthToken } from './utils/auth'; 


// --- Configuration ---
const BACKEND_URL = 'https://api.blackroq.pro'; 

// --- Création du Contexte ---
const SocketContext = createContext({
    socket: null, 
    notifications: [], 
    unreadCount: 0,
    fetchNotifications: () => Promise.resolve(), 
    markAllAsRead: () => Promise.resolve(), 
    markAsRead: () => Promise.resolve(), 
    deleteNotification: () => Promise.resolve(), 
    sendEvent: () => {} 
});


export const useSocket = () => {
    return useContext(SocketContext);
};


export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]); 
    const [unreadCount, setUnreadCount] = useState(0); 
    
    const socketRef = useRef(null); // Utilisé pour maintenir une référence stable


    // ----------------------------------------------------
    // API REST LOGIC (Historique et Actions - Fonctions useCallback)
    // ----------------------------------------------------

    // 🚀 1. Récupère l'historique complet
    const fetchNotifications = useCallback(async () => {
        const token = getAuthToken();
        if (!token) return;

        try {
            const res = await fetch(`${BACKEND_URL}/api/notifications`, {
                headers: {
                    'Authorization': `Bearer ${token}` 
                }
            });
            const data = await res.json();
            
            if (res.ok) {
                setNotifications(data);
                const initialUnread = data.filter(n => !n.read).length;
                setUnreadCount(initialUnread); 
            } else {
                console.error("Échec du chargement de l'historique:", data);
            }
        } catch (err) {
            console.error("Erreur API lors du chargement des notifications:", err);
        }
    }, []); 

    // 🚀 2. Marque toutes les notifications comme lues
    const markAllAsRead = useCallback(async () => {
        const token = getAuthToken();
        if (!token) return;

        try {
            const res = await fetch(`${BACKEND_URL}/api/notifications/read-all`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (res.ok) {
                setNotifications(prevNotifs => prevNotifs.map(n => ({ ...n, read: true })));
                setUnreadCount(0);
            } else {
                console.error("Échec de la mise à jour 'read-all'.");
            }
        } catch (err) {
            console.error("Erreur API lors de la mise à jour 'read-all':", err);
        }
    }, []);

    // 🚀 3. Marque UNE SEULE notification comme lue
    const markAsRead = useCallback(async (notifId) => {
        const token = getAuthToken();
        if (!token || !notifId) return;

        let notificationWasUnread = false;

        // Mise à jour optimiste du Frontend
        setNotifications(prevNotifs => 
            prevNotifs.map(n => {
                if (n._id === notifId && !n.read) {
                    notificationWasUnread = true;
                    return { ...n, read: true };
                }
                return n;
            })
        );
        if (notificationWasUnread) {
             setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }


        try {
            const res = await fetch(`${BACKEND_URL}/api/notifications/${notifId}/read`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!res.ok) {
                console.error(`Échec de la mise à jour 'read' pour ${notifId}.`);
            }
        } catch (err) {
            console.error("Erreur API lors de la mise à jour 'read':", err);
        }
    }, []); 
    
    // 🚀 4. Supprime une notification par son ID via l'API REST
    const deleteNotification = useCallback(async (notifId) => {
        const token = getAuthToken();
        if (!token || !notifId) return;

        let wasUnread = false;

        // Mise à jour optimiste : Supprimer immédiatement du Frontend
        setNotifications(prevNotifs => 
            prevNotifs.filter(n => {
                if (n._id === notifId && !n.read) {
                    wasUnread = true; // Vérifie si la notif supprimée était non lue
                }
                return n._id !== notifId;
            })
        );
        
        // Mettre à jour le compteur de non-lus si la notif supprimée n'était pas lue
        if (wasUnread) {
             setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }


        try {
            const res = await fetch(`${BACKEND_URL}/api/notifications/${notifId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!res.ok) {
                console.error(`Échec de la suppression pour ${notifId}.`);
            }
        } catch (err) {
            console.error("Erreur API lors de la suppression:", err);
        }
    }, []);


    // ----------------------------------------------------
    // SOCKET.IO LOGIC
    // ----------------------------------------------------

    const connectSocket = useCallback(() => {
        const token = getAuthToken();
        
        // 1. Si pas de token, on déconnecte s'il y a un socket actif, puis on sort.
        if (!token) {
            console.log("Aucun token trouvé. La connexion socket ne sera pas établie.");
            if (socketRef.current) {
                 socketRef.current.disconnect();
                 socketRef.current = null;
                 setSocket(null);
            }
            return () => {}; 
        }

        // 2. Si un socket est déjà actif et connecté, on ne fait rien.
        if (socketRef.current && socketRef.current.connected) {
            return () => {};
        }

        console.log("Tentative d'établissement d'une nouvelle connexion socket...");
        
        const newSocket = io(BACKEND_URL, {
            query: { token }, 
            transports: ['websocket', 'polling'], 
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            forceNew: false, 
        });
        
        setSocket(newSocket);
        socketRef.current = newSocket; 


        // 1. Écouteurs de connexion/déconnexion/erreur (inchangés)
        newSocket.on('connect', () => {
            console.log(`Socket.IO connecté. ID: ${newSocket.id}`);
        });
        newSocket.on('disconnect', (reason) => {
            console.log('Socket.IO déconnecté. Raison :', reason);
        });
        newSocket.on('connect_error', (err) => {
            console.error("Erreur de connexion Socket.IO :", err.message);
        });


        // 2. Écouteurs de notifications (gestion temps réel - inchangés)
        const notificationHandler = (newNotification) => {
            console.log("Nouvelle notification reçue :", newNotification);
            
            setNotifications(prevNotifs => [newNotification, ...prevNotifs]);
            
            if (!newNotification.read) {
                setUnreadCount(prevCount => prevCount + 1);
            }
        };
        
        newSocket.on("newNotification", notificationHandler);
        newSocket.on("notification", notificationHandler);


        // 3. Fonction de nettoyage (cleanup)
        return () => {
             // ✅ CORRECTION CRITIQUE : Suppression de newSocket.disconnect()
             // Seuls les écouteurs sont retirés pour éviter les fuites de mémoire.
             // Le socket reste actif pendant les navigations si l'utilisateur est authentifié.
             console.log('Nettoyage des écouteurs de socket.');
             newSocket.off("newNotification", notificationHandler);
             newSocket.off("notification", notificationHandler);
             newSocket.off('connect');
             newSocket.off('disconnect');
             newSocket.off('connect_error');
        }
    }, []); 


    // ----------------------------------------------------
    // Effets principaux
    // ----------------------------------------------------

    useEffect(() => {
        const cleanupConnection = connectSocket();
        // C'est le retour de connectSocket (qui ne déconnecte plus le socket)
        return cleanupConnection; 
    }, [connectSocket]); 
    
    
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]); 


    // Fonction utilitaire pour envoyer des événements
    const sendEvent = useCallback((event, data) => {
        if (socketRef.current && socketRef.current.connected) {
            socketRef.current.emit(event, data);
            console.log(`Événement envoyé: ${event}`);
        } else {
            console.warn("Socket non connecté. Impossible d'envoyer l'événement:", event);
        }
    }, []);


    const contextValue = { 
        socket, 
        notifications,
        unreadCount, 
        fetchNotifications,
        markAllAsRead, 
        markAsRead, 
        deleteNotification, 
        sendEvent 
    };


    return (
        <SocketContext.Provider value={contextValue}>
            {children}
        </SocketContext.Provider>
    );
};